import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Popconfirm,
  Typography,
  Card,
  AutoComplete,
  Spin,
  Tooltip,
  Alert,
  Divider
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, LoadingOutlined, InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import customerService from '../services/customerService'
import itemMappingService from '../services/itemMappingService'
import sosItemService from '../services/sosItemService'

const { Title } = Typography
const { Option } = Select
const { TextArea } = Input

function ItemMappingsPage() {
  const [mappings, setMappings] = useState([])
  const [customers, setCustomers] = useState([])
  const [sosItems, setSosItems] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [bulkModalVisible, setBulkModalVisible] = useState(false)
  const [editingMapping, setEditingMapping] = useState(null)
  const [form] = Form.useForm()
  const [bulkForm] = Form.useForm()
  const [pageSize, setPageSize] = useState(10)
  const [bulkFile, setBulkFile] = useState(null)
  const [bulkUploading, setBulkUploading] = useState(false)
  const [bulkImportResult, setBulkImportResult] = useState(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [clearCustomerModalOpen, setClearCustomerModalOpen] = useState(false)
  const [clearCustomerConfirmText, setClearCustomerConfirmText] = useState('')
  const searchTimeoutRef = useRef(null)

  useEffect(() => {
    fetchCustomers()
  }, [])

  useEffect(() => {
    if (selectedCustomer) {
      fetchMappings()
      setSelectedRowKeys([])
    }
  }, [selectedCustomer])

  const fetchCustomers = async () => {
    try {
      const data = await customerService.getAll()
      setCustomers(data)
      if (data.length > 0) {
        setSelectedCustomer(data[0].customerId)
      }
    } catch (error) {
      message.error('Failed to load customers')
    }
  }

  const fetchMappings = async () => {
    setLoading(true)
    try {
      const data = await itemMappingService.getAll(selectedCustomer)
      setMappings(data)
    } catch (error) {
      message.error('Failed to load item mappings')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (!value || value.length < 2) {
      setSosItems([])
      setSearching(false)
      return
    }

    setSearching(true)
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const data = await sosItemService.search(value)
        setSosItems(data || [])
      } catch (error) {
        console.error('Search error:', error)
        message.error('Failed to search SOS items')
      } finally {
        setSearching(false)
      }
    }, 500) // 500ms debounce
  }

  const handleAdd = () => {
    setEditingMapping(null)
    form.resetFields()
    form.setFieldsValue({ customerId: selectedCustomer })
    setModalVisible(true)
  }

  const handleEdit = (record) => {
    setEditingMapping(record)
    form.setFieldsValue(record)
    setModalVisible(true)
  }

  const handleDelete = async (id) => {
    try {
      await itemMappingService.delete(id)
      message.success('Mapping deleted successfully')
      fetchMappings()
    } catch (error) {
      message.error('Failed to delete mapping')
    }
  }

  const handleDeleteSelected = async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) return
    try {
      await itemMappingService.bulkDelete(selectedRowKeys)
      message.success(`Deleted ${selectedRowKeys.length} mapping(s)`)
      setSelectedRowKeys([])
      fetchMappings()
    } catch (error) {
      message.error('Failed to delete selected mappings')
    }
  }

  const handleClearCustomerMappings = async () => {
    if (!selectedCustomer) return
    try {
      const resp = await itemMappingService.clearCustomer(selectedCustomer)
      message.success(`Cleared ${resp?.deleted ?? 0} mapping(s)`)
      setSelectedRowKeys([])
      fetchMappings()
      setClearCustomerModalOpen(false)
      setClearCustomerConfirmText('')
    } catch (error) {
      message.error('Failed to clear mappings for customer')
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (editingMapping) {
        await itemMappingService.update(editingMapping.mappingId, values)
        message.success('Mapping updated successfully')
      } else {
        await itemMappingService.create(values)
        message.success('Mapping created successfully')
      }
      setModalVisible(false)
      fetchMappings()
    } catch (error) {
      if (error.errorFields) {
        return
      }
      message.error('Failed to save mapping')
    }
  }

  const handleBulkImport = async (fileOverride = null) => {
    try {
      // Prefer file upload if provided (CSV/XLSX). Otherwise, fallback to textarea import.
      const fileToUse = fileOverride || bulkFile
      if (fileToUse) {
        setBulkUploading(true)
        const resp = await itemMappingService.bulkImportFile(selectedCustomer, fileToUse)
        const imported = resp?.imported ?? 0
        const updated = resp?.updated ?? 0
        const failed = resp?.failed ?? 0
        setBulkImportResult(resp || { imported, updated, failed, results: [] })
        message.success(`Bulk import complete. Imported: ${imported}, Updated: ${updated}, Failed: ${failed}`)
      } else {
        const values = await bulkForm.validateFields()
        const mappingsText = values.mappings || ''
        const lines = mappingsText.split('\n').filter((line) => line.trim())

        const mappings = lines.map((line) => {
          const [customerItemName, customerItemNumber, sosItemId, sosItemName] = line
            .split(',')
            .map((s) => s.trim())
          return { customerItemName, customerItemNumber, sosItemId, sosItemName }
        })

        const resp = await itemMappingService.bulkImport(selectedCustomer, mappings)
        const imported = resp?.imported ?? mappings.length
        const updated = resp?.updated ?? 0
        const failed = resp?.failed ?? 0
        setBulkImportResult(resp || { imported, updated, failed, results: [] })
        message.success(`Bulk import complete. Imported: ${imported}, Updated: ${updated}, Failed: ${failed}`)
      }
      fetchMappings()
    } catch (error) {
      if (error.errorFields) {
        return
      }
      message.error('Failed to import mappings')
    } finally {
      setBulkUploading(false)
    }
  }

  const columns = [
    {
      title: 'Customer Item Name',
      dataIndex: 'customerItemName',
      key: 'customerItemName'
    },
    {
      title: 'Customer Item Number',
      dataIndex: 'customerItemNumber',
      key: 'customerItemNumber'
    },
    {
      title: 'SOS Item ID',
      dataIndex: 'sosItemId',
      key: 'sosItemId'
    },
    {
      title: 'SOS Item Name',
      dataIndex: 'sosItemName',
      key: 'sosItemName'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to delete this mapping?"
            onConfirm={() => handleDelete(record.mappingId)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const customerName =
    customers.find((c) => c.customerId === selectedCustomer)?.customerName || ''

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}
        >
          <div style={{ flex: '1 1 300px' }}>
            <Title level={2} style={{ margin: 0 }}>
              Item Mappings - {customerName}
            </Title>
          </div>
          <Space wrap>
            <Select
              style={{ width: 200 }}
              value={selectedCustomer || undefined}
              onChange={setSelectedCustomer}
              placeholder="Select Customer"
            >
              {customers.map((customer) => (
                <Option key={customer.customerId} value={customer.customerId}>
                  {customer.customerName}
                </Option>
              ))}
            </Select>
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} selected mapping(s)?`}
              description="This cannot be undone."
              okText="Delete"
              cancelText="Cancel"
              onConfirm={handleDeleteSelected}
              disabled={selectedRowKeys.length === 0}
            >
              <Button danger disabled={selectedRowKeys.length === 0}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
            <Button
              danger
              type="dashed"
              disabled={!selectedCustomer}
              onClick={() => setClearCustomerModalOpen(true)}
            >
              Clear Customer Mappings
            </Button>
            <Button icon={<UploadOutlined />} onClick={() => setBulkModalVisible(true)}>
              Bulk Import
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Mapping
            </Button>
          </Space>
        </div>

        <Card bodyStyle={{ padding: 0 }}>
          <Table
            dataSource={mappings}
            columns={columns}
            rowKey="mappingId"
            loading={loading}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys)
            }}
            pagination={{ 
              pageSize: pageSize, 
              size: 'small',
              showSizeChanger: true,
              onShowSizeChange: (current, size) => setPageSize(size),
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </Space>

      <Modal
        title={editingMapping ? 'Edit Mapping' : 'Add Mapping'}
        open={modalVisible}
        onOk={handleSubmit}
        confirmLoading={loading}
        onCancel={() => setModalVisible(false)}
        width={window.innerWidth < 768 ? '95%' : 600}
        style={{ top: 20 }}
      >
        <Alert
          message="Mapping Instructions"
          description={
            <ul style={{ paddingLeft: '20px', marginBottom: 0 }}>
              <li><strong>Customer Item Name:</strong> Exact name from the PO PDF (Required).</li>
              <li><strong>Customer Item Number:</strong> Item ID/SKU used by the customer (Optional).</li>
              <li><strong>SOS Item Name:</strong> The target item in your SOS Inventory (Required).</li>
            </ul>
          }
          type="info"
          showIcon
          icon={<QuestionCircleOutlined />}
          style={{ marginBottom: '20px' }}
        />
        
        <Form form={form} layout="vertical">
          <Form.Item name="customerId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="customerItemName"
            label={
              <Space>
                Customer Item Name
                <Tooltip title="The description exactly as it appears on the purchase order PDF.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Please enter customer item name' }]}
          >
            <Input placeholder="e.g., SPACER;END STUD" />
          </Form.Item>

          <Form.Item 
            name="customerItemNumber" 
            label={
              <Space>
                Customer Item Number
                <Tooltip title="The item number or part number used by the customer.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
          >
            <Input placeholder="e.g., 40093562 (Optional)" />
          </Form.Item>

          <Form.Item
            name="sosItemName"
            label={
              <Space>
                SOS Item Name (Target)
                <Tooltip title="Search and select the matching item from your SOS inventory.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Please select SOS item' }]}
            help={searching ? "Searching SOS inventory..." : "Type at least 2 chars to search"}
          >
            <AutoComplete
              options={sosItems.map((item) => ({
                key: item.itemId,
                value: item.itemName,
                label: `${item.itemName} (ID: ${item.itemId})`,
                itemId: item.itemId
              }))}
              onSearch={handleSearch}
              onSelect={(value, option) => {
                // Auto-populate SOS Item ID when item is selected
                form.setFieldsValue({
                  sosItemId: option.itemId
                })
              }}
              placeholder="Start typing to search SOS..."
              notFoundContent={searching ? <Spin size="small" /> : "No items found"}
            >
              <Input 
                suffix={searching ? <LoadingOutlined spin /> : null} 
              />
            </AutoComplete>
          </Form.Item>

          <Form.Item
            name="sosItemId"
            label="SOS Item ID (Read-only)"
            rules={[{ required: true, message: 'SOS item ID is required' }]}
          >
            <Input placeholder="Auto-populated from search" disabled />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Clear Customer Mappings"
        open={clearCustomerModalOpen}
        onCancel={() => {
          setClearCustomerModalOpen(false)
          setClearCustomerConfirmText('')
        }}
        okText="Delete"
        okButtonProps={{ danger: true, disabled: clearCustomerConfirmText.trim().toLowerCase() !== 'delete' }}
        onOk={handleClearCustomerMappings}
      >
        <p>
          This will permanently delete <strong>all</strong> item mappings for the selected customer.
          To confirm, type <strong>delete</strong> below.
        </p>
        <Input
          value={clearCustomerConfirmText}
          onChange={(e) => setClearCustomerConfirmText(e.target.value)}
          placeholder="Type delete to confirm"
          autoFocus
        />
      </Modal>

      <Modal
        title="Bulk Import Mappings"
        open={bulkModalVisible}
        confirmLoading={bulkUploading}
        onCancel={() => {
          setBulkModalVisible(false)
          bulkForm.resetFields()
          setBulkFile(null)
          setBulkImportResult(null)
        }}
        width={window.innerWidth < 768 ? '95%' : 700}
        style={{ top: 20 }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setBulkModalVisible(false)
              bulkForm.resetFields()
              setBulkFile(null)
              setBulkImportResult(null)
            }}
          >
            Close
          </Button>
        ]}
      >
        <Alert
          message="File Format (Excel/CSV)"
          description={
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
              <li>
                <strong>Header row:</strong> optional (it will be skipped if detected).
              </li>
              <li>
                <strong>Expected columns (in order):</strong>
                <ol style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
                  <li><strong>Customer Item Code</strong> (optional) — e.g. <code>40093563</code></li>
                  <li><strong>Customer Item Description</strong> (required) — exact text you expect from the PO PDF</li>
                  <li><strong>SOS Item ID</strong> (optional) — if provided, this is the primary match</li>
                  <li><strong>SOS Item Description</strong> (optional) — used only when SOS ID is empty (search by description)</li>
                </ol>
              </li>
              <li>
                <strong>SOS matching rule:</strong> if SOS ID is filled → use it; otherwise search by SOS description (must be unambiguous).
              </li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={bulkForm} layout="vertical">
          <Form.Item label="Upload CSV / Excel (Recommended)">
            <Upload
              accept=".csv,.xlsx"
              maxCount={1}
              beforeUpload={(file) => {
                setBulkFile(file)
                setBulkImportResult(null)
                // Prevent auto-upload; we upload on modal OK so we can tie to selectedCustomer
                // Now: run import immediately on file selection (no separate Import button).
                setTimeout(() => {
                  handleBulkImport(file)
                }, 0)
                return false
              }}
              onRemove={() => {
                setBulkFile(null)
              }}
              fileList={bulkFile ? [bulkFile] : []}
            >
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="mappings"
            label="Mappings (CSV Format)"
            rules={[
              () => ({
                validator: async (_, value) => {
                  if (bulkFile) return
                  if (value && String(value).trim()) return
                  throw new Error('Please upload a file OR paste mappings text')
                }
              })
            ]}
            extra="Text paste alternative (one per line): CustomerItemName, CustomerItemNumber, SosItemId, SosItemName"
          >
            <TextArea
              rows={10}
              placeholder={`SPACER;END STUD, 40093562, 657, Spacer-EndStud-2024
RING;MSKNG,LWR, 40093563, 658, Masking-Ring-Lower`}
            />
          </Form.Item>
        </Form>

        {bulkImportResult && (
          <>
            <Divider />
            <Alert
              type={bulkImportResult.failed > 0 ? 'warning' : 'success'}
              showIcon
              message="Bulk Import Results"
              description={
                <div>
                  <div>
                    <strong>Imported:</strong> {bulkImportResult.imported ?? 0}{' '}
                    <strong style={{ marginLeft: 12 }}>Updated:</strong> {bulkImportResult.updated ?? 0}{' '}
                    <strong style={{ marginLeft: 12 }}>Failed:</strong> {bulkImportResult.failed ?? 0}{' '}
                    <strong style={{ marginLeft: 12 }}>Total:</strong> {bulkImportResult.total ?? 0}
                  </div>
                  <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.65)' }}>
                    Rows are kept here as a table so you can review and copy errors (the modal will not auto-close).
                  </div>
                </div>
              }
            />

            <div style={{ marginTop: 12 }}>
              <Table
                size="small"
                rowKey={(r) => `${r.row}-${r.status}`}
                dataSource={bulkImportResult.results || []}
                pagination={{ pageSize: 10, size: 'small', showSizeChanger: true }}
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Row', dataIndex: 'row', key: 'row', width: 70 },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    width: 110,
                    render: (s) => {
                      const val = String(s || '')
                      const color = val === 'failed' ? 'red' : val === 'updated' ? 'blue' : 'green'
                      return <span style={{ color, fontWeight: 600 }}>{val}</span>
                    }
                  },
                  { title: 'Item Code', dataIndex: 'customerItemNumber', key: 'customerItemNumber', width: 140 },
                  { title: 'Item Description', dataIndex: 'customerItemName', key: 'customerItemName', width: 260 },
                  { title: 'SOS ID', dataIndex: 'sosItemId', key: 'sosItemId', width: 100 },
                  { title: 'SOS Description', dataIndex: 'sosItemName', key: 'sosItemName', width: 260 },
                  {
                    title: 'SOS Match',
                    dataIndex: 'sosMatch',
                    key: 'sosMatch',
                    width: 140,
                    render: (v) => {
                      const val = String(v || '')
                      if (!val || val === 'unknown') return ''
                      if (val === 'id') return 'Matched by SOS ID'
                      if (val === 'description') return 'Matched by SOS Description'
                      if (val === 'id+description') return 'Matched by ID + Description'
                      return val
                    }
                  },
                  { title: 'Failure Reason', dataIndex: 'reason', key: 'reason', width: 320 }
                ]}
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}

export default ItemMappingsPage
